import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { waApi, type WaChat } from '../../api/whatsapp'
import { usePageTitle } from '../../hooks/usePageTitle'
import { ChatListColumn } from './ChatListColumn'
import { ConversationPane } from './ConversationPane'
import { ChatDetailsPanel } from './ChatDetailsPanel'
import { NewChatModal } from './NewChatModal'
import { LinkOrCreateLeadModal } from './LinkOrCreateLeadModal'

export function WhatsAppPage() {
  usePageTitle('WhatsApp')
  const { companyId } = useParams<{ companyId: string }>()
  const [params, setParams] = useSearchParams()
  const chatId = params.get('chat')
  const [sessionId, setSessionId] = useState<string>('')
  const [showNewChat, setShowNewChat] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [pendingLink, setPendingLink] = useState<{ chat: WaChat; phone: string } | null>(null)

  const { data: sessions = [] } = useQuery({
    queryKey: ['wa-sessions', companyId],
    queryFn: () => waApi.listSessions(companyId!),
    enabled: !!companyId,
  })
  const connected = sessions.filter(s => s.status === 'connected')

  if (sessions.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        No WhatsApp number connected yet. Owners can connect one in Settings → WhatsApp.
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      <ChatListColumn
        companyId={companyId!}
        sessions={sessions}
        sessionId={sessionId}
        onSessionChange={setSessionId}
        activeChatId={chatId}
        onSelect={id => setParams({ chat: id })}
        onNewChat={() => setShowNewChat(true)}
      />
      {chatId ? (
        <>
          <ConversationPane
            companyId={companyId!}
            chatId={chatId}
            onToggleDetails={() => setShowDetails(v => !v)}
            onClose={() => { setShowDetails(false); setParams({}) }}
          />
          {showDetails && (
            <ChatDetailsPanel
              companyId={companyId!}
              chatId={chatId}
              onClose={() => setShowDetails(false)}
            />
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center text-gray-400">
          Select a chat or start a new one
        </div>
      )}
      {showNewChat && (
        <NewChatModal
          companyId={companyId!}
          sessions={connected}
          onClose={() => setShowNewChat(false)}
          onCreated={c => {
            setShowNewChat(false)
            setParams({ chat: c.id })
          }}
          onNeedsLeadLink={(c, phone) => {
            // Open the conversation, then prompt to link/create a lead.
            setShowNewChat(false)
            setParams({ chat: c.id })
            setPendingLink({ chat: c, phone })
          }}
        />
      )}
      {pendingLink && (
        <LinkOrCreateLeadModal
          companyId={companyId!}
          chat={pendingLink.chat}
          typedPhone={pendingLink.phone}
          onClose={() => setPendingLink(null)}
        />
      )}
    </div>
  )
}
